import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react"
import type {
  BaseQueryApi,
  BaseQueryFn,
  FetchArgs,
} from "@reduxjs/toolkit/query"
import { dynamicMiddlewaresInstance, registerReducer } from "@/core/store"
import type { RootState } from "@/core/store"
import { baseQueryWithReauth } from "@/modules/app/store"
import { postBotSessionIdentity } from "../../utils/sessionIdentity"

export const postBotTagTypes = [
  "Template",
  "TemplateVersion",
  "Publication",
  "Placement",
  "Media",
  "Occurrence",
  "PlacementHistory",
] as const

export const postBotTags = {
  templates: { type: "Template", id: "LIST" } as const,
  template: (id: string) => ({ type: "Template" as const, id }),
  versions: (templateId: string) => ({
    type: "TemplateVersion" as const,
    id: `LIST:${templateId}`,
  }),
  version: (id: string) => ({ type: "TemplateVersion" as const, id }),
  publications: { type: "Publication", id: "LIST" } as const,
  publication: (id: string) => ({ type: "Publication" as const, id }),
  placements: { type: "Placement", id: "LIST" } as const,
  placement: (id: string) => ({ type: "Placement" as const, id }),
  mediaList: { type: "Media", id: "LIST" } as const,
  media: (id: string) => ({ type: "Media" as const, id }),
  occurrence: (id: string) => ({ type: "Occurrence" as const, id }),
  history: (id: string) => ({ type: "PlacementHistory" as const, id }),
}

const root = new URL(
  import.meta.env.REACT_APP_POSTBOT_API,
  window.location.origin,
).toString()
export const postBotApiBaseUrl = root.endsWith("/") ? root : `${root}/`

const baseQuery = fetchBaseQuery({
  baseUrl: postBotApiBaseUrl,
  prepareHeaders: (headers, { getState }) => {
    const token = (getState() as RootState).auth.token
    if (token) headers.set("authorization", `Bearer ${token}`)
    return headers
  },
})

// Only the existing auth wrapper may retry a rejected 401. Never retry a send
// on a network, dependency or delivery-unknown error.
// Also used directly for ephemeral media capabilities, outside RTK actions/cache.
export const postBotTransport = baseQueryWithReauth(baseQuery, {
  getSessionIdentity: postBotSessionIdentity,
})
export const businessApi = createApi({
  reducerPath: "postBotBusinessApi",
  tagTypes: postBotTagTypes,
  baseQuery: postBotTransport,
  endpoints: () => ({}),
})

dynamicMiddlewaresInstance.addMiddleware(businessApi.middleware)
registerReducer(businessApi.reducerPath, businessApi.reducer)

/** Convert local DTO construction failures into normal RTK errors, before HTTP. */
export const requestQuery =
  <Args, Result, WireResult = Result>(
    buildRequest: (args: Args) => FetchArgs,
    transformResponse?: (response: WireResult) => Result,
  ) =>
  async (
    args: Args,
    _api: BaseQueryApi,
    _extra: unknown,
    query: (args: FetchArgs) => ReturnType<BaseQueryFn>,
  ): Promise<Awaited<ReturnType<BaseQueryFn<FetchArgs, Result>>>> => {
    let request: FetchArgs
    try {
      request = buildRequest(args)
    } catch (error) {
      return {
        error: {
          status: "CUSTOM_ERROR",
          error:
            error instanceof Error ? error.message : "Invalid PostBot request",
        },
      }
    }
    const result = await query(request)
    // RTK does not support endpoint transformResponse with queryFn. Apply it
    // here before caching, and leave transport/local errors untouched.
    if (transformResponse && result.error === undefined) {
      return {
        data: transformResponse(result.data as WireResult),
        meta: result.meta,
      }
    }
    return result as Awaited<ReturnType<BaseQueryFn<FetchArgs, Result>>>
  }

/** PATCH fields are explicit DTO keys, not keys from a response object. */
export const assertPatch = (revision: number, mutableValues: unknown[]) => {
  assertRevision(revision)
  if (!mutableValues.some((value) => value !== undefined))
    throw new TypeError("PATCH requires a mutable field")
}

export const assertRevision = (revision: number) => {
  if (!Number.isSafeInteger(revision) || revision < 1)
    throw new TypeError("Revision must be a positive safe integer")
}
